import { customAlphabet } from 'nanoid';

import { RoomScheduleData } from '../../../../../types/survey';
import {
  AddEditRoomProps,
  BaseRoomScheduleRoomProps,
  RoomScheduleRoomProps,
} from '../../../../../types/room';

const getRandomID = () => {
  return customAlphabet(
    '1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
    25,
  )();
};

const removeDuplicatesFromArray = <T>(array: T[]) => {
  return Array.from(new Set(array));
};

export const roomScheduleImportColumns = [
  'floor',
  'name',
  'areaFloor',
  'areaWall',
  'notes',
];

export const roomScheduleSetupExportHeaders = [
  'Site Name',
  'Building Name',
  'Floor Name',
  'Room Name',
  'Area Floor',
  'Area Wall',
  'Notes',
];

export function isValidRoomScheduleHeaderCount(headerCount: number) {
  return [
    roomScheduleImportColumns.length - 1,
    roomScheduleImportColumns.length,
  ].includes(headerCount);
}

export function getRoomList(
  newList: RoomScheduleRoomProps[],
  previousList?: RoomScheduleData,
): { rooms: BaseRoomScheduleRoomProps[]; floors: string[] } {
  const getIdFromPreviousRoomOrNew = (room) => {
    const match = previousList?.find((r, index) => {
      return r.name === room.name && r.floor === room.floor;
    });
    return match?.id || getRandomID();
  };

  const listWithCuid = newList
    .map((room) => ({
      name: String(room.name).trim(),
      floor: String(room.floor).trim(),
      areaFloor: Number(room.areaFloor) || 0,
      areaWall: Number(room.areaWall) || 0,
      notes: String(room.notes || '').trim(),
      id: getIdFromPreviousRoomOrNew(room),
      deleted: 0,
    }))
    .filter((room) => room.name && room.floor);

  const floors = removeDuplicatesFromArray(listWithCuid.map((r) => r.floor));

  return {
    rooms: listWithCuid,
    floors,
  };
}

export function getRoomScheduleSaveUpdate({
  roomData,
  roomScheduleData,
  editingRoomId,
  getId = getRandomID,
}: {
  roomData: AddEditRoomProps;
  roomScheduleData: RoomScheduleData;
  editingRoomId?: string;
  getId?: () => string;
}): { data: BaseRoomScheduleRoomProps[]; floors: string[] } {
  const roomListWithoutRoomBeingEdited = editingRoomId
    ? roomScheduleData.filter((r) => r.id !== editingRoomId)
    : roomScheduleData;

  const roomDataWithAdditionalProps: BaseRoomScheduleRoomProps = {
    name: roomData.name.trim(),
    floor: roomData.floor.trim(),
    areaFloor: Number(roomData.areaFloor),
    areaWall: Number(roomData.areaWall),
    notes: String(roomData.notes || '').trim(),
    id: editingRoomId || getId(),
    deleted: 0,
  };
  const floorLastIndex = roomListWithoutRoomBeingEdited.findLastIndex(
    (r) => r.floor === roomDataWithAdditionalProps.floor,
  );
  const data = [...roomListWithoutRoomBeingEdited];

  if (floorLastIndex < 0) {
    data.push(roomDataWithAdditionalProps);
  } else {
    data.splice(floorLastIndex + 1, 0, roomDataWithAdditionalProps);
  }

  return {
    data,
    floors: removeDuplicatesFromArray(data.map((r) => r.floor)),
  };
}

export function getRoomScheduleExportData({
  roomScheduleData,
  siteName,
  buildingName,
}: {
  roomScheduleData?: RoomScheduleData;
  siteName?: string;
  buildingName?: string;
}) {
  return {
    headers: roomScheduleSetupExportHeaders,
    rows: roomScheduleData?.map((roomData) => ({
      siteName,
      buildingName,
      floor: roomData.floor,
      name: roomData.name,
      areaFloor: roomData.areaFloor,
      areaWall: roomData.areaWall,
      notes: String(roomData.notes || '').trim(),
    })),
  };
}
